import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ContentChild,
  effect,
  ElementRef,
  HostBinding,
  Inject,
  input,
  Input,
  OnInit,
  PLATFORM_ID,
  TemplateRef,
  viewChild,
  ViewEncapsulation
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import { ENVIRONMENT } from '@environment';
import { LanguageSelectorComponent } from '@usheru-hff/core/language-selector';
import { HffCommonUiUsheruLogoModule } from '@usheru-hff/hff-common/ui-usheru-logo';
import { ShowtimeStreamingSubscribeComponent } from '@usheru-hff/movie-detail/feature-subscribe';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { NavigationService } from '@usheru-hff/sdk/navigation';
import { TRACKING_SERVICE, TrackingService } from '@usheru-hff/sdk/tracking';
import { Channel, PageLink, SocialNetworkLink } from '@usheru-hff/shared/domain';
import { SharedFeatureSocialLinksModule } from '@usheru-hff/shared/feature-social-links';
import { SharedUtilsModule, TemplatesService } from '@usheru-hff/shared/utils';
import { SharedUtilsImageModule } from '@usheru-hff/shared/utils-image';

import { FooterLocalizePipe } from '@usheru-hff/ui/footer-localize';

import { AcontraEnvironment } from '../../environments/acontra-environment';

@Component({
  selector: 'acon-footer',
  templateUrl: './acon-footer.component.html',
  styleUrls: ['./acon-footer.component.scss'],
  imports: [
    CommonModule,
    SharedUtilsImageModule,
    ShowtimeStreamingSubscribeComponent,
    SharedFeatureSocialLinksModule,
    RouterModule,

    SharedUtilsModule,
    LanguageSelectorComponent,
    HffCommonUiUsheruLogoModule,
    FooterLocalizePipe
  ],
  encapsulation: ViewEncapsulation.None
})
export class AconFooterComponent implements OnInit {
  divActiveCampaign = viewChild<ElementRef>('formActiveCampaign');
  @Input() transparentBackground?: false;
  @ContentChild(TemplateRef) customTemplate: TemplateRef<unknown>;
  @HostBinding('attr.data-custom-footer') customFooter = true;

  /**
   * Input used in the Movie Sites to set the channel logo alternative.
   * TODO implement
   */
  mainLogoAlternative = input<string | undefined>(undefined);
  /**
   * Input used in the Movie Sites to set the channel logo drop shadow effect.
   * TODO implement
   */
  mainLogoDropShadowEffect = input<string | undefined>('white');

  logoUrl: string;
  hasSocialLinks: boolean;
  socialLinks: SocialNetworkLink[];
  listOfVariables;

  links: PageLink[];

  channel: Channel;

  emailSubscriptionSignupEnabled: boolean;
  footerAccreditationsLogoUrl =
    'https://cdnstatic.usheru.com/img/channels/www.acontracorrientefilms.com/footer/acontrafilms-footer-logos-4.png';
  generalitatFooterLogoUrl = 'assets/Generalitat_catala.png';

  // footer custom link template
  footerCustomLinkTemplate: TemplateRef<unknown>;

  // footer instance
  footerInstance: unknown;

  private scriptLoaded = false;
  private diffuserScriptLoaded = false;

  constructor(
    private channelService: ChannelService,
    private navigationService: NavigationService,
    private router: Router,
    @Inject(ENVIRONMENT) private env: AcontraEnvironment,
    private templateService: TemplatesService,
    @Inject(TRACKING_SERVICE) private trackingService: TrackingService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.channel = this.channelService.channel;
    this.emailSubscriptionSignupEnabled = this.channelService?.properties?.emailSubscriptionSignupEnabled;

    this.hasSocialLinks = !env?.customPageParams?.headerSocialLinks || env?.customPageParams?.footerSocialLinks;

    effect(() => {
      if (isPlatformBrowser(this.platformId) && this.divActiveCampaign() && !this.scriptLoaded) {
        this.loadActiveCampaignScript();
      }
      if (isPlatformBrowser(this.platformId) && !this.diffuserScriptLoaded) {
        this.loadDiffuserScript();
      }
    });
  }

  ngOnInit() {
    this.logoUrl = this.channel.logoUrl;
    this.links = this.navigationService.getFooterLinks();
    this.footerInstance = this;

    if (this.channel.socialNetworks) {
      this.socialLinks = this.channel.socialNetworks;
    }

    this.listOfVariables = {
      logoUrl: this.logoUrl,
      links: this.links,
      socialLinks: this.socialLinks,
      transparentBackground: this.transparentBackground,
      hasSocialLinks: this.hasSocialLinks,
      copyrightMsg: this.copyrightMsg,
      hasTranslationFooter: this.hasTranslationFooter
    };

    this.loadFooterCustomTemplate();
  }

  loadActiveCampaignScript(): void {
    if (!this.scriptLoaded) {
      const script = document.createElement('script');
      script.src = 'https://acontracorrientefilms.activehosted.com/f/embed.php?id=22';
      script.charset = 'utf-8';
      script.async = true;
      document.body.appendChild(script);
      this.scriptLoaded = true;
    }
  }

  loadDiffuserScript(): void {
    if (!this.diffuserScriptLoaded) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.text = `
        (function(e,t,o,n,p,r,i){
          e.visitorGlobalObjectAlias=n;
          e[e.visitorGlobalObjectAlias]=e[e.visitorGlobalObjectAlias]||function(){
            (e[e.visitorGlobalObjectAlias].q=e[e.visitorGlobalObjectAlias].q||[]).push(arguments)
          };
          e[e.visitorGlobalObjectAlias].l=(new Date).getTime();
          r=t.createElement("script");
          r.src=o;
          r.async=true;
          i=t.getElementsByTagName("script")[0];
          i.parentNode.insertBefore(r,i)
        })(window,document,"https://diffuser-cdn.app-us1.com/diffuser/diffuser.js","vgo");
  
        vgo('setAccount', '478687827');
        vgo('setTrackByDefault', true);
        vgo('process');
      `;
      document.body.appendChild(script);
      this.diffuserScriptLoaded = true;
    }
  }
  get copyrightMsg() {
    return this.env?.copyrightMsg;
  }

  get hasTranslationFooter() {
    const hasTranslationFooter = this.env.customPageParams?.translationFooter;
    if (typeof hasTranslationFooter !== 'undefined') {
      return hasTranslationFooter;
    }
    return false;
  }

  get showGeneralitatFooterLogo() {
    const movieSlug = this.currentMovieDetailSlug;
    return !!movieSlug && (this.env.components?.aconFooter?.generalitatLogoMovieSlugs ?? []).includes(movieSlug);
  }

  private get currentMovieDetailSlug() {
    const path = this.router.url.split(/[?#]/)[0];
    const segments = path.split('/').filter(Boolean);

    if (segments.length === 2 && segments[0] === 'movie') {
      return decodeURIComponent(segments[1]);
    }

    return undefined;
  }

  loadFooterCustomTemplate() {
    if (this.env.templateRefs?.footerCustomLinkTemplate) {
      this.footerCustomLinkTemplate = this.templateService.get(this.env.templateRefs.footerCustomLinkTemplate);
    }
  }

  onFooterLinkClicked(link: PageLink) {
    this.trackingService.trackEvent({
      action: 'FooterLink',
      properties: {
        category: 'Footer',
        label: link.name
      }
    });
  }
}
